import React, { useState } from 'react';
import { useForm } from '../context/FormContext';
import { isFutureDate, isWithin90Days } from '../utils/validation';
import { US_STATES } from '../utils/states';
import { compressLicensePhoto } from '../utils/imageCompress';

export default function Step08_License({ onNext, onBack }) {
  const { state, dispatch } = useForm();
  const [errs, setErrs] = useState({});
  const [processingPhoto, setProcessingPhoto] = useState(false);
  const lic = state.license;

  const set = (field, val) => {
    dispatch({ type: 'SET_NESTED', key: 'license', value: { [field]: val } });
    setErrs(e => ({ ...e, [field]: '' }));
  };

  const toggleState = (code) => {
    // Individual selection is meaningless while Lower 48 is checked -- the grid
    // is disabled in that state, but guard here too so a stray call can't
    // create the contradictory "Lower 48 AND a partial list" record.
    if (lic.lower48) return;
    const current = lic.statesOperated;
    const updated = current.includes(code) ? current.filter(s => s !== code) : [...current, code];
    set('statesOperated', updated);
    setErrs(e => ({ ...e, statesOperated: '' }));
  };

  // Lower 48 is a STANDALONE selection, not a shortcut that ticks 48 states.
  // It mirrors the checkbox on the existing paper form: an OTR driver marks it
  // instead of recalling five years of individual states. Checking it CLEARS any
  // states already tapped, so the submission can never carry both a blanket
  // claim and a contradicting partial list. Unchecking restores an empty grid --
  // the driver's prior taps are deliberately not resurrected, because after a
  // round trip through the blanket claim they are no longer a reliable answer.
  const toggleLower48 = (checked) => {
    dispatch({
      type: 'SET_NESTED', key: 'license',
      value: { lower48: checked, statesOperated: [] },
    });
    setErrs(e => ({ ...e, statesOperated: '' }));
  };

  const handlePhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { setErrs(v => ({ ...v, photo: 'File must be under 10MB' })); return; }
    if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) { setErrs(v => ({ ...v, photo: 'Only JPG or PNG files accepted' })); return; }

    // Downscale before storing. This is what keeps the submit request small
    // enough to finish on a weak connection. compressLicensePhoto never
    // rejects — on any failure it returns the original image.
    setProcessingPhoto(true);
    try {
      const { base64 } = await compressLicensePhoto(file);
      if (!base64) {
        setErrs(v => ({ ...v, photo: 'Could not read that image — please try another photo' }));
        return;
      }
      dispatch({ type: 'SET_NESTED', key: 'license', value: { photoFile: file, photoBase64: base64, photoName: file.name } });
      setErrs(v => ({ ...v, photo: '' }));
    } finally {
      setProcessingPhoto(false);
    }
  };

  const next = () => {
    const e = {};
    if (!lic.number.trim()) e.number = 'License number required';
    if (!lic.state) e.state = 'State required';
    if (!lic.issueDate) e.issueDate = 'Issue date required';
    if (!lic.class.trim()) e.class = 'License class required';
    if (!lic.expirationDate) e.expirationDate = 'Expiration date required';
    else if (!isFutureDate(lic.expirationDate)) e.expirationDate = 'License is expired — please provide current license';
    if (!lic.yearsHeld && lic.yearsHeld !== 0) e.yearsHeld = 'Years held required';
    if (!lic.endorsements.trim()) e.endorsements = 'Required — enter "None" if none';
    if (!lic.restrictions.trim()) e.restrictions = 'Required — enter "None" if none';
    // Either answer satisfies the field: the Lower 48 checkbox OR at least one
    // individual state. Both empty is the only failure.
    if (!lic.lower48 && lic.statesOperated.length === 0) {
      e.statesOperated = 'Select at least one state, or check Lower 48';
    }
    if (!lic.photoBase64) e.photo = 'License photo is required';
    if (Object.keys(e).length) { setErrs(e); return; }
    onNext();
  };

  const expiring = lic.expirationDate && isWithin90Days(lic.expirationDate);

  return (
    <div className="step-card">
      <h2 className="step-title">Driver's License</h2>
      <div className="field-row">
        <div className="field-group">
          <label className="field-label">License Number <span className="required">*</span></label>
          <input className={`field-input ${errs.number ? 'error' : ''}`} value={lic.number}
            onChange={e => set('number', e.target.value)} placeholder="License #" />
          {errs.number && <div className="field-error">{errs.number}</div>}
        </div>
        <div className="field-group">
          <label className="field-label">Issuing State <span className="required">*</span></label>
          <select className={`field-select ${errs.state ? 'error' : ''}`} value={lic.state}
            onChange={e => set('state', e.target.value)}>
            <option value="">Select...</option>
            {US_STATES.map(s => <option key={s.code} value={s.code}>{s.code}</option>)}
          </select>
          {errs.state && <div className="field-error">{errs.state}</div>}
        </div>
      </div>
      <div className="field-row">
        <div className="field-group">
          <label className="field-label">Issue Date <span className="required">*</span></label>
          <input type="date" className={`field-input ${errs.issueDate ? 'error' : ''}`} value={lic.issueDate}
            onChange={e => set('issueDate', e.target.value)} />
          {errs.issueDate && <div className="field-error">{errs.issueDate}</div>}
        </div>
        <div className="field-group">
          <label className="field-label">Expiration Date <span className="required">*</span></label>
          <input type="date" className={`field-input ${errs.expirationDate ? 'error' : ''}`} value={lic.expirationDate}
            onChange={e => set('expirationDate', e.target.value)} />
          {errs.expirationDate && <div className="field-error">{errs.expirationDate}</div>}
        </div>
      </div>
      {expiring && !errs.expirationDate && (
        <div className="warning-banner">⚠️ This license expires within 90 days. Please be aware you may need to renew soon.</div>
      )}
      <div className="field-row">
        <div className="field-group">
          <label className="field-label">License Class <span className="required">*</span></label>
          <select className={`field-select ${errs.class ? 'error' : ''}`} value={lic.class}
            onChange={e => set('class', e.target.value)}>
            <option value="">Select...</option>
            <option>A</option><option>B</option><option>C</option><option>D</option>
          </select>
          {errs.class && <div className="field-error">{errs.class}</div>}
        </div>
        <div className="field-group">
          <label className="field-label">Years Held <span className="required">*</span></label>
          <input type="number" min="0" className={`field-input ${errs.yearsHeld ? 'error' : ''}`} value={lic.yearsHeld}
            onChange={e => set('yearsHeld', e.target.value)} placeholder="Years" inputMode="numeric" />
          {errs.yearsHeld && <div className="field-error">{errs.yearsHeld}</div>}
        </div>
      </div>
      <div className="field-group">
        <label className="field-label">Endorsements <span className="required">*</span></label>
        <input className={`field-input ${errs.endorsements ? 'error' : ''}`} value={lic.endorsements}
          onChange={e => set('endorsements', e.target.value)} placeholder='e.g. "H, X" or "None"' />
        {errs.endorsements && <div className="field-error">{errs.endorsements}</div>}
      </div>
      <div className="field-group">
        <label className="field-label">Restrictions <span className="required">*</span></label>
        <input className={`field-input ${errs.restrictions ? 'error' : ''}`} value={lic.restrictions}
          onChange={e => set('restrictions', e.target.value)} placeholder='e.g. "Corrective lenses" or "None"' />
        {errs.restrictions && <div className="field-error">{errs.restrictions}</div>}
      </div>
      <div className="field-group">
        <label className="field-label">States Operated In (Last 5 Years) <span className="required">*</span> — tap to select</label>

        {/* Lower 48 -- the same selection that exists on the paper sheet. An OTR
            driver marks this instead of recalling five years of states, which is
            not a realistic thing to ask. It does NOT tick the 48 individually,
            and the output prints "Lower 48" rather than an enumerated list. */}
        <div className="field-group" style={{
          background: '#f4f6f9', border: '1px solid var(--border)', borderRadius: 8,
          padding: 12, marginBottom: 12
        }}>
          <label className="radio-option" style={{ margin: 0, alignItems: 'flex-start' }}>
            <input type="checkbox" checked={!!lic.lower48}
              onChange={e => toggleLower48(e.target.checked)}
              style={{ marginTop: 3 }} />
            <span style={{ marginLeft: 8 }}>
              <strong>Lower 48</strong>
              <div className="field-hint" style={{ marginTop: 4 }}>
                Check this if you have run over-the-road throughout the continental
                U.S. You do not need to select individual states.
              </div>
            </span>
          </label>
        </div>

        {lic.lower48
          ? <div className="field-hint">Selected: Lower 48</div>
          : <>
              <div className={`tag-select-wrap ${errs.statesOperated ? 'error' : ''}`}>
                {US_STATES.map(s => (
                  <span key={s.code} className={`tag-option ${lic.statesOperated.includes(s.code) ? 'selected' : ''}`}
                    onClick={() => toggleState(s.code)}>{s.code}</span>
                ))}
              </div>
              {lic.statesOperated.length > 0 && <div className="field-hint">Selected: {lic.statesOperated.join(', ')}</div>}
            </>}
        {errs.statesOperated && <div className="field-error">{errs.statesOperated}</div>}
      </div>
      <div className="field-group">
        <label className="field-label">License Photo <span className="required">*</span></label>
        <label className={`file-upload-wrap ${lic.photoBase64 ? 'has-file' : ''}`}>
          <input type="file" accept="image/jpeg,image/png" onChange={handlePhoto} />
          {processingPhoto
            ? <div>⏳ Processing photo…<br /><small style={{ color: 'var(--text-light)' }}>Just a moment</small></div>
            : lic.photoBase64
              ? <div>✅ {lic.photoName}<br /><small style={{ color: 'var(--text-light)' }}>Tap to replace</small></div>
              : <div>📷 Tap to upload license photo<br /><small style={{ color: 'var(--text-light)' }}>JPG or PNG, max 10MB</small></div>}
        </label>
        {errs.photo && <div className="field-error">{errs.photo}</div>}
      </div>
      <div className="wizard-nav" style={{ position: 'static', borderTop: 'none', padding: 0, marginTop: 8 }}>
        <button className="btn-back" onClick={onBack}>← Back</button>
        <button className="btn-next" onClick={next} disabled={processingPhoto}>
          {processingPhoto ? 'Processing photo…' : 'Next →'}
        </button>
      </div>
    </div>
  );
}
